import { Theme } from '@emotion/react';
import { AddCircleOutline } from '@mui/icons-material';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteIcon from 'assets/delete-red.png';
import EditIcon from 'assets/edit.svg';
import { ReactComponent as VerticalDots } from 'assets/vertical-dots.svg';
import { ReactComponent as ViewIcon } from 'assets/view.svg';
import { relationshipColorMap } from 'components/featured/ZoomInfo/BusinessSelected/Contacts/data';
import { Relationship } from 'components/featured/ZoomInfo/BusinessSelected/Contacts/types';
import CustomDeleteModal from 'components/shared/CustomDeleteModal/CustomDeleteModal';
import Pagination from 'components/shared/CustomPagination/CustomPagination';
import CustomPopOver from 'components/shared/CustomPopOver/CustomPopOver';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import SectionLoader from 'components/shared/SectionLoader';
import { SortPopover } from 'components/shared/SortPopOver';
import { Contact } from 'model/contacts.model';
import React, { useState } from 'react';
import { modalReducer } from 'reducers/modalReducer';
import { ContactService } from 'service/contact.service';
import zeplinColor from 'theme/Color';
import { Utilities } from 'utilities/utilities';
import { muiStyles as relationshipChipStyles } from '../ContactsGrid/ContactCard/muiStyles';

type ContactsTableProps = {
  contacts: Contact[];
  onDelete?: (contact: 'delete', onComplete?: string) => void;
  onViewDetails?: (contact: Contact) => void;
  onSort?: (field: string, order: 'asc' | 'desc') => void;
  rowsPerPage?: number;
  setRowsPerPage?: (value: number) => void;
  currentPage?: number;
  setCurrentPage?: (value: number) => void;
  totalCount?: number;
  isLoading?: boolean;
  onEdit?: (contact: any) => void;
  variant?: 'default' | 'business';
  onAddToMyContacts?: (contact: Contact) => Promise<void>;
};

export const ContactsTable = ({
  contacts,
  onDelete,
  onViewDetails,
  onSort,
  rowsPerPage = 10,
  setRowsPerPage,
  currentPage = 0,
  setCurrentPage,
  totalCount = 0,
  isLoading = false,
  onEdit,
  variant = 'default',
  onAddToMyContacts,
}: ContactsTableProps) => {
  const [anchorEl, setAnchorEl] = useState<SVGSVGElement | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [modalState, modalDispatch] = React.useReducer(modalReducer, {});
  const [nameSortAnchorEl, setNameSortAnchorEl] = useState<null | HTMLElement>(null);
  const [roleSortAnchorEl, setRoleSortAnchorEl] = useState<null | HTMLElement>(null);
  const [companySortAnchorEl, setCompanySortAnchorEl] = useState<null | HTMLElement>(null);
  const [emailSortAnchorEl, setEmailSortAnchorEl] = useState<null | HTMLElement>(null);
  const [lastContactedSortAnchorEl, setLastContactedSortAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [statusSortAnchorEl, setStatusSortAnchorEl] = useState<null | HTMLElement>(null);
  const [nameSortOption, setNameSortOption] = useState<'asc' | 'desc'>('asc');
  const [roleSortOption, setRoleSortOption] = useState<'asc' | 'desc'>('asc');
  const [companySortOption, setCompanySortOption] = useState<'asc' | 'desc'>('asc');
  const [emailSortOption, setEmailSortOption] = useState<'asc' | 'desc'>('asc');
  const [lastContactedSortOption, setLastContactedSortOption] = useState<'asc' | 'desc'>('desc');
  const [statusSortOption, setStatusSortOption] = useState<'asc' | 'desc'>('asc');
  const [pageWindowStart, setPageWindowStart] = useState(0);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const handleFirstPage = () => {
    if (setCurrentPage) {
      setCurrentPage(0);
      setPageWindowStart(0);
    }
  };

  const handlePreviousPage = () => {
    if (setCurrentPage && currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (setCurrentPage && currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleLastPage = () => {
    if (setCurrentPage) {
      setCurrentPage(totalPages - 1);
      const maxWindowStart = Math.max(0, totalPages - 4);
      setPageWindowStart(maxWindowStart);
    }
  };

  const handlePageChangeClick = (page: number) => {
    if (setCurrentPage) {
      setCurrentPage(page);
    }
  };

  const handleRowsPerPageChangeClick = (value: number) => {
    if (setRowsPerPage) {
      setRowsPerPage(value);
    }
    if (setCurrentPage) {
      setCurrentPage(0);
    }
  };

  const handleOptionsClick = (e: React.MouseEvent<SVGSVGElement>, contact: Contact) => {
    e.stopPropagation();
    setSelectedContact(contact);
    setAnchorEl(e.currentTarget);
  };

  const handleViewDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedContact && onViewDetails) {
      onViewDetails(selectedContact);
    }
    setAnchorEl(null);
  };

  const handleEditContact = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
    onEdit?.(selectedContact);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    modalDispatch({ type: 'OPEN', payload: 'delete' });
    setAnchorEl(null);
  };

  const handleAddToMyContacts = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selectedContact || !onAddToMyContacts) return;
    try {
      await onAddToMyContacts(selectedContact);
    } catch (error) {
      console.error(error);
    }
    setAnchorEl(null);
  };

  const handleConfirmDelete = async () => {
    try {
      if (selectedContact) {
        await ContactService.deleteContact(selectedContact.contactId);
        modalDispatch({ type: 'CLOSE', payload: 'delete' });
        if (onDelete) {
          onDelete('delete', 'Contact deleted successfully');
        }
      }
    } catch (error) {
      if (onDelete) {
        onDelete('delete', 'Failed to delete contact');
      }
    }
  };

  const handleNameSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setNameSortAnchorEl(event.currentTarget);
  };

  const handleRoleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setRoleSortAnchorEl(event.currentTarget);
  };

  const handleCompanySortClick = (event: React.MouseEvent<HTMLElement>) => {
    setCompanySortAnchorEl(event.currentTarget);
  };

  const handleEmailSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setEmailSortAnchorEl(event.currentTarget);
  };

  const handleLastContactedSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setLastContactedSortAnchorEl(event.currentTarget);
  };

  const handleStatusSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setStatusSortAnchorEl(event.currentTarget);
  };

  const handleSortOption = (
    field: string,
    order: 'asc' | 'desc',
    setAnchor: React.Dispatch<React.SetStateAction<HTMLElement | null>>,
  ) => {
    if (onSort) {
      onSort(field, order);
    }
    setAnchor(null);
  };

  const handleNameSortChange = (value: 'asc' | 'desc') => {
    setNameSortOption(value);
    handleSortOption('name', value, setNameSortAnchorEl);
  };

  const handleRoleSortChange = (value: 'asc' | 'desc') => {
    setRoleSortOption(value);
    handleSortOption('roles', value, setRoleSortAnchorEl);
  };

  const handleCompanySortChange = (value: 'asc' | 'desc') => {
    setCompanySortOption(value);
    handleSortOption('company', value, setCompanySortAnchorEl);
  };

  const handleEmailSortChange = (value: 'asc' | 'desc') => {
    setEmailSortOption(value);
    handleSortOption('email', value, setEmailSortAnchorEl);
  };

  const handleLastContactedSortChange = (value: 'asc' | 'desc') => {
    setLastContactedSortOption(value);
    handleSortOption('lastContacted', value, setLastContactedSortAnchorEl);
  };

  const handleStatusSortChange = (value: 'asc' | 'desc') => {
    setStatusSortOption(value);
    const field = variant === 'business' ? 'contactRelationship' : 'status';
    handleSortOption(field, value, setStatusSortAnchorEl);
  };

  const getContactName = (contact: Contact) => {
    return contact.name || 'N/A';
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (date: string) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    const day = d.getDate();
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    const year = d.getFullYear();
    return `${day} ${month}, ${year}`;
  };

  if (isLoading) {
    return <SectionLoader />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
      <TableContainer
        sx={{ borderRadius: '14px', maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}
      >
        <Table stickyHeader sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow
              sx={{
                borderBottom: `1px solid ${zeplinColor.Neutral5}`,
                '& th': {
                  backgroundColor: zeplinColor.Neutral3,
                  fontWeight: 600,
                  color: zeplinColor.BasicBlack,
                },
                '& th:first-of-type': {
                  borderRadius: '14px 0px 0px 14px',
                },
                '& th:last-child': {
                  borderRadius: '0px 14px 14px 0px',
                },
              }}
            >
              <TableCell
                sx={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                    Name
                  </Typography>
                  <IconButton size='small' onClick={handleNameSortClick} sx={{ padding: '2px' }}>
                    <UnfoldMoreIcon sx={{ fontSize: '16px', color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell
                align='left'
                sx={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                    Role
                  </Typography>
                  <IconButton size='small' onClick={handleRoleSortClick} sx={{ padding: '2px' }}>
                    <UnfoldMoreIcon sx={{ fontSize: '16px', color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell
                align='left'
                sx={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                    Company
                  </Typography>
                  <IconButton size='small' onClick={handleCompanySortClick} sx={{ padding: '2px' }}>
                    <UnfoldMoreIcon sx={{ fontSize: '16px', color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell
                align='left'
                sx={{
                  padding: '12px 16px',
                }}
              >
                <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                  Phone No
                </Typography>
              </TableCell>
              <TableCell
                align='left'
                sx={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                    Email
                  </Typography>
                  <IconButton size='small' onClick={handleEmailSortClick} sx={{ padding: '2px' }}>
                    <UnfoldMoreIcon sx={{ fontSize: '16px', color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell
                align='left'
                sx={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                    Last Contacted
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={handleLastContactedSortClick}
                    sx={{ padding: '2px' }}
                  >
                    <UnfoldMoreIcon sx={{ fontSize: '16px', color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell
                align='left'
                sx={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                    {variant === 'business' ? 'Relationship' : 'Status'}
                  </Typography>
                  <IconButton size='small' onClick={handleStatusSortClick} sx={{ padding: '2px' }}>
                    <UnfoldMoreIcon sx={{ fontSize: '16px', color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell
                align='center'
                sx={{
                  fontWeight: 600,
                  color: zeplinColor.BasicBlack,
                  padding: '12px 16px',
                  fontSize: '14px',
                  width: '50px',
                }}
              >
                {' '}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.length === 0 ? (
              <TableRow sx={{ borderBottom: `1px solid ${zeplinColor.Neutral5}` }}>
                <TableCell colSpan={8} sx={{ padding: '24px 16px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img src='/assets/leads-icon.svg' style={{ width: '88px', height: '88px' }} />
                    <Typography
                      variant='responsiveHeading3'
                      color={zeplinColor.BasicBlack}
                      sx={{ marginTop: { xs: '22px', xl: '24px' } }}
                    >
                      No Contacts Yet
                    </Typography>
                    <Typography
                      variant='responsiveBody3'
                      color={zeplinColor.Neutral8}
                      sx={{
                        width: '70%',
                        textAlign: 'center',
                        marginTop: { xs: '10px', xl: '12px' },
                      }}
                    >
                      Looks like there are no contacts here yet. New contacts will appear once
                      added.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              contacts.map((contact, index) => (
                <TableRow
                  key={contact.contactId}
                  onClick={() => onViewDetails?.(contact)}
                  sx={{
                    borderBottom: `1px solid ${zeplinColor.Neutral5}`,
                    '&:hover': {
                      backgroundColor: zeplinColor.Neutral2,
                      cursor: 'pointer',
                    },
                  }}
                >
                  <TableCell sx={{ padding: '12px 16px', fontSize: '14px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {contact?.profileImageUrl ? (
                        <Avatar
                          src={contact?.profileImageUrl}
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: zeplinColor.BasicBlack,
                            fontSize: '12px',
                            fontWeight: 600,
                          }}
                        >
                          {getInitials(getContactName(contact))}
                        </Avatar>
                      ) : (
                        <Box
                          sx={{
                            borderRadius: '24px',

                            width: { xs: '24px', xl: '24px' },
                            height: { xs: '24px', xl: '24px' },
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: zeplinColor.Brand1,
                          }}
                        >
                          <Typography variant='responsiveLabel1' color={zeplinColor.Neutral1}>
                            {contact?.name
                              ?.split(' ')
                              .map((value: any, index: number) =>
                                contact?.name &&
                                (index === 0 || index === contact?.name?.split(' ').length - 1)
                                  ? value[0]
                                  : '',
                              )}
                          </Typography>
                        </Box>
                      )}
                      <Typography variant='body3' sx={{ color: zeplinColor.BasicBlack }}>
                        {getContactName(contact)}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ padding: '12px 16px', fontSize: '14px' }}>
                    <Typography variant='body3' sx={{ color: zeplinColor.BasicBlack }}>
                      {contact?.roles || 'N/A'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ padding: '12px 16px', fontSize: '14px' }}>
                    <Typography variant='body3' sx={{ color: zeplinColor.BasicBlack }}>
                      {contact?.company || 'N/A'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ padding: '12px 16px', fontSize: '14px' }}>
                    <Typography variant='body3' sx={{ color: zeplinColor.BasicBlack }}>
                      {contact?.phone || 'N/A'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ padding: '12px 16px', fontSize: '14px' }}>
                    <Typography variant='body3' sx={{ color: zeplinColor.BasicBlack }}>
                      {contact?.email || 'N/A'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ padding: '12px 16px', fontSize: '14px' }}>
                    <Typography variant='body3' sx={{ color: zeplinColor.BasicBlack }}>
                      {variant === 'business' && contact.lastContacted
                        ? formatDate(contact.lastContacted)
                        : 'N/A'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ fontSize: '14px' }}>
                    {variant === 'business' ? (
                      <Chip
                        label={
                          <Typography
                            variant='responsiveLabel1'
                            sx={relationshipChipStyles.chipLabel}
                          >
                            {Utilities.formatSnakeToTitle(
                              (contact.contactRelationship ?? 'Known').replace(/\s+/g, '_'),
                            )}
                          </Typography>
                        }
                        sx={
                          [
                            relationshipColorMap[
                              (contact.contactRelationship ?? 'Known') as Relationship
                            ] ?? relationshipColorMap.Known,
                            relationshipChipStyles.chip,
                          ] as SxProps<Theme>
                        }
                      />
                    ) : (
                      <Chip
                        label={
                          <Typography
                            variant='responsiveLabel1'
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            Active
                          </Typography>
                        }
                        sx={{
                          bgcolor: zeplinColor.Success,
                          color: zeplinColor.Neutral1,
                          '&.MuiChip-root': {
                            width: 'fit-content',
                            padding: '4px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: { xs: '18px', xl: '22px' },

                            borderRadius: '50px',
                          },
                          '& .MuiChip-label': {
                            padding: 0,
                          },
                        }}
                      />
                    )}
                  </TableCell>

                  <TableCell align='center' sx={{ padding: '12px 16px' }}>
                    <LightTooltip
                      title={
                        <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                          Options
                        </Typography>
                      }
                      placement='bottom-end'
                      style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                    >
                      <VerticalDots
                        onClick={(e) => handleOptionsClick(e, contact)}
                        style={{ cursor: 'pointer', width: '20px', height: '20px' }}
                      />
                    </LightTooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Pagination
        setCurrentPage={setCurrentPage || (() => {})}
        setRowsPerPage={setRowsPerPage || (() => {})}
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        count={totalCount}
        paginatedDataLength={contacts.length}
        onFirstPage={handleFirstPage}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        onLastPage={handleLastPage}
        onPageChange={handlePageChangeClick}
        onRowsPerPageChange={handleRowsPerPageChangeClick}
        pageWindowStart={pageWindowStart}
        setPageWindowStart={setPageWindowStart}
      />

      <Box>
        <CustomPopOver
          onClick={(e: any) => {
            e.stopPropagation();
          }}
          setAnchorEl={setAnchorEl}
          anchorEl={anchorEl}
          id={'simple-popover'}
          sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              padding: '16px',
            }}
          >
            {variant === 'business' ? (
              <Box
                sx={{ display: 'flex', gap: '9px', cursor: 'pointer', alignItems: 'center' }}
                onClick={handleAddToMyContacts}
              >
                <AddCircleOutline sx={{ fontSize: 18 }} />
                <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                  Add to My contacts
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
                  onClick={handleViewDetails}
                >
                  <ViewIcon width={18} height={18} />
                  <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                    View details
                  </Typography>
                </Box>
                <Box
                  sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
                  onClick={handleEditContact}
                >
                  <img src={EditIcon} width={18} height={18} alt='edit' />
                  <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                    Edit contact
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={handleDelete}>
                  <img src={DeleteIcon} width={18} height={18} alt='delete' />
                  <Typography variant='responsiveBody3' color={zeplinColor.Error}>
                    Delete
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </CustomPopOver>
      </Box>

      <SortPopover
        anchorEl={nameSortAnchorEl}
        setAnchorEl={setNameSortAnchorEl}
        onSortChange={handleNameSortChange}
        sortOption={nameSortOption}
        label='Name'
        ascLabel='A to Z'
        descLabel='Z to A'
      />
      <SortPopover
        anchorEl={roleSortAnchorEl}
        setAnchorEl={setRoleSortAnchorEl}
        onSortChange={handleRoleSortChange}
        sortOption={roleSortOption}
        label='Role'
        ascLabel='A to Z'
        descLabel='Z to A'
      />
      <SortPopover
        anchorEl={companySortAnchorEl}
        setAnchorEl={setCompanySortAnchorEl}
        onSortChange={handleCompanySortChange}
        sortOption={companySortOption}
        label='Company'
        ascLabel='A to Z'
        descLabel='Z to A'
      />
      <SortPopover
        anchorEl={emailSortAnchorEl}
        setAnchorEl={setEmailSortAnchorEl}
        onSortChange={handleEmailSortChange}
        sortOption={emailSortOption}
        label='Email'
        ascLabel='A to Z'
        descLabel='Z to A'
      />
      <SortPopover
        anchorEl={lastContactedSortAnchorEl}
        setAnchorEl={setLastContactedSortAnchorEl}
        onSortChange={handleLastContactedSortChange}
        sortOption={lastContactedSortOption}
        label='Last Contacted'
        ascLabel='Oldest to Newest'
        descLabel='Newest to Oldest'
      />
      <SortPopover
        anchorEl={statusSortAnchorEl}
        setAnchorEl={setStatusSortAnchorEl}
        onSortChange={handleStatusSortChange}
        sortOption={statusSortOption}
        label={variant === 'business' ? 'Relationship' : 'Status'}
        ascLabel={variant === 'business' ? 'A to Z' : 'Active to Inactive'}
        descLabel={variant === 'business' ? 'Z to A' : 'Inactive to Active'}
      />

      <CustomDeleteModal
        isModalOpen={modalState.delete}
        modalDispatch={modalDispatch}
        onDelete={handleConfirmDelete}
        title='Delete Contact'
        description='Are you sure you want to delete this contact? This action cannot be undone.'
      />
    </Box>
  );
};
